import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import { shiftDay } from '../utils/date-utils';
import { bandExtent } from '../VehicleSchedule/band-runs';
import {
  VehicleScheduleResponse,
  VehicleScheduleEvent,
  ScheduleVehicle,
  VehicleAssignment,
  VehicleUnavailability,
  VehicleScheduleTour,
  VehicleScheduleCrew,
  ForecastPoint,
} from '../VehicleSchedule/types';

/**
 * A proposed tour change surfaced AFTER a drag/assign creates or breaks
 * same-vehicle adjacency. Nothing happens to a tour without the user confirming
 * this in a dialog (Mannie: "everything explicit"). Neighbor-scoped — only the
 * contiguous run containing the changed event is considered; untouched tours are
 * never altered.
 */
export type TourProposal =
  | { kind: 'create'; vehicleId: string; eventIds: string[]; eventNames: string[] }
  | { kind: 'extend'; vehicleId: string; tourId: string; tourName: string; eventIds: string[]; eventNames: string[] }
  | { kind: 'dissolve'; vehicleId: string; tourId: string; tourName: string; eventId: string; eventName: string; willDissolve: boolean };

/** Normalize date strings to YYYY-MM-DD (strip any time component). */
const normDate = (d: string | null) => (d ? d.slice(0, 10) : d);

/**
 * POST /vehicle-schedule/reassign reports per-change failures INSIDE a 200, as
 * `{ added, removed, collapsed, errors: [{ change, error }] }`. Nothing throws,
 * so a caller that only awaits the promise cannot tell a rejection from a
 * success. Returns the message to show, or null when the batch really landed.
 *
 * Kept next to the reads rather than inside them because assign and unassign
 * both need it and drifted apart once already.
 */
const reassignError = (res: any): string | null => {
  const errors = res?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first = errors[0];
  return (typeof first === 'string' ? first : first?.error) || 'That change was rejected.';
};

const normalizeEvents = (events: VehicleScheduleEvent[]): VehicleScheduleEvent[] =>
  (events || []).map(e => ({
    ...e,
    event_date: normDate(e.event_date) as string,
    end_date: normDate(e.end_date),
    ship_out_date: normDate(e.ship_out_date),
    ship_back_date: normDate(e.ship_back_date),
  }));

const normalizeUnavailability = (rows: VehicleUnavailability[]): VehicleUnavailability[] =>
  (rows || []).map(r => ({
    ...r,
    start_date: normDate(r.start_date) as string,
    end_date: normDate(r.end_date) as string,
  }));

type Result = { ok: boolean; error?: string };

/**
 * Transient drag state for a band's left/right edge. Dragging EDITS the event's
 * ship date: the left edge sets `ship_out_date`, the right edge `ship_back_date`.
 * `evStart`/`evEnd` are the show span — a left drag must stay ≤ evStart, a right
 * drag ≥ evEnd (you can't pull the leave/return inside the show).
 */
export interface VehicleDragState {
  vehicleId: string;
  eventId: string;
  edge: 'left' | 'right';
  evStart: string;
  evEnd: string;
  /** The edge that ISN'T moving — anchors the preview extent. */
  anchorDate: string;
  /** Pointer-mapped target date for the moving edge. */
  previewDate: string;
  /** True if the target would cross into the show span (illegal). */
  invalid: boolean;
}

/**
 * A band-BODY drag: moving a show (or a whole tour) from one vehicle row to
 * another. Distinct from VehicleDragState, which is an EDGE drag that edits ship
 * dates — this one changes which truck covers the show and touches no dates.
 *
 * The authoritative copy of this rides in the drag's dataTransfer and is read on
 * drop. The store copy exists ONLY so lanes can highlight themselves valid or
 * invalid during dragover, where the spec forbids reading dataTransfer.
 *
 * windowStart/windowEnd are the band's VISIBLE extent, used for the lane's
 * maintenance-block fast path. The server re-checks against every affected show's
 * real window, including tour shows outside the grid.
 */
export interface BandDragState {
  eventId: string;
  fromVehicleId: string;
  scope: 'event' | 'tour';
  label: string;
  windowStart: string;
  windowEnd: string;
}

interface VehicleScheduleState {
  windowFrom: string;
  windowTo: string;

  vehicles: ScheduleVehicle[];
  events: VehicleScheduleEvent[];
  assignments: VehicleAssignment[];
  /** Crew per event with the days each person covers, for the band hover card.
   *  Days come from event_crew_shifts only — see VehicleScheduleCrew. */
  crew: VehicleScheduleCrew[];
  unavailability: VehicleUnavailability[];
  tours: VehicleScheduleTour[];
  /** Availability forecast over the window, aligned to the grid days. Refreshed
   *  on every (quiet) refetch so the strip moves the instant a tour changes. */
  forecast: ForecastPoint[];
  /** True when the last fetch could not compute the forecast — drives the strip's
   *  explicit "unavailable" state (distinct from a genuinely empty series). */
  forecastError: boolean;

  drag: VehicleDragState | null;
  /** In-flight band-body drag, for lane drop-target highlighting only. */
  bandDrag: BandDragState | null;

  /** Pending tour change awaiting the user's confirmation (null = none). */
  tourProposal: TourProposal | null;

  loading: boolean;
  saving: boolean;
  error: string | null;

  /** Stashed so mutations can reuse the page's auth-redirect callback. */
  _onUnauthorized?: () => void;

  fetchWindow: (
    from: string,
    to: string,
    onUnauthorized?: () => void,
    opts?: { quiet?: boolean }
  ) => Promise<void>;

  // Assignment (event-level, immediate). `detectTour` (default true) runs the
  // auto-tour adjacency check after — the rental band's "Assign vehicle (keeps
  // transport)" passes false so it doesn't pop a tour prompt the user didn't ask for.
  assignVehicle: (eventId: string, vehicleId: string, opts?: { detectTour?: boolean }) => Promise<Result>;
  unassignVehicle: (eventId: string, vehicleId: string) => Promise<Result>;
  /** Toggle whether a show's external transport is arranged — moves it between
   *  the unsorted top band and the packed Shipping / Rentals lanes. */
  setTransportArranged: (eventId: string, arranged: boolean) => Promise<Result>;
  /** Switch a bottom-lane band's flavor: 'shipping' (ship-out legs) vs 'driving'
   *  (drive-rental legs). */
  setTransportMode: (eventId: string, mode: 'shipping' | 'driving') => Promise<Result>;
  /** Save the freetext leg notes on a bottom-lane event (outbound / return). */
  setTransportLegs: (eventId: string, legs: { out: string | null; back: string | null }) => Promise<Result>;

  // Drag → edit the event's ship date
  startDrag: (drag: VehicleDragState) => void;
  updateDrag: (previewDate: string, invalid: boolean) => void;
  commitDrag: () => void;
  cancelDrag: () => void;
  editShipDate: (eventId: string, field: 'ship_out_date' | 'ship_back_date', value: string) => Promise<Result>;

  // Drag the band BODY → move the show (or its whole tour) to another vehicle
  startBandDrag: (drag: BandDragState) => void;
  endBandDrag: () => void;
  /** Atomic move via POST /vehicle-schedule/move. A tour scope resolves its event
   *  set server-side, so shows outside the visible window move too. */
  moveAssignment: (payload: { eventId: string; fromVehicleId: string; scope: 'event' | 'tour' }, toVehicleId: string) => Promise<Result & { moved?: number; scope?: string }>;

  // Leg notes
  updateAssignment: (id: string, updates: Partial<VehicleAssignment>) => Promise<Result>;

  // Maintenance (asset_unavailability)
  createBlock: (assetId: string, startDate: string, endDate: string, notes: string) => Promise<Result>;
  deleteBlock: (id: string) => Promise<Result>;

  // Auto-tour (rides existing /tours endpoints; invoked after a confirm dialog)
  detectTourProposal: (vehicleId: string, eventId: string) => void;
  clearTourProposal: () => void;
  createTourFromEvents: (eventIds: string[]) => Promise<Result>;
  extendTour: (tourId: string, eventIds: string[]) => Promise<Result>;
  removeEventFromTour: (tourId: string, eventId: string) => Promise<Result>;
  /** Atomically split a tour at a boundary show. 'left' detaches everything left
   *  of it, 'right' everything right (boundary stays on the staying side). */
  splitTour: (tourId: string, boundaryEventId: string, side: 'left' | 'right') => Promise<Result>;
}

/** Sentinel "vehicle id" for the rental lane — rentals aren't real vehicles, so
 *  ship-date drags inside the rental lane carry this and skip tour detection. */
export const RENTAL_VEHICLE_ID = '__rental__';

const useVehicleScheduleStore = create<VehicleScheduleState>((set, get) => ({
  windowFrom: '',
  windowTo: '',
  vehicles: [],
  events: [],
  assignments: [],
  crew: [],
  unavailability: [],
  tours: [],
  forecast: [],
  forecastError: false,
  drag: null,
  bandDrag: null,
  tourProposal: null,
  loading: false,
  saving: false,
  error: null,

  fetchWindow: async (from, to, onUnauthorized, opts = {}) => {
    const { quiet = false } = opts;
    if (!quiet) set({ loading: true, error: null });
    if (onUnauthorized) set({ _onUnauthorized: onUnauthorized });
    try {
      const data: VehicleScheduleResponse = await apiFetchAuthed(
        `/vehicle-schedule?from=${from}&to=${to}`,
        { onUnauthorized: onUnauthorized || get()._onUnauthorized }
      );
      set({
        windowFrom: from,
        windowTo: to,
        vehicles: data.vehicles || [],
        events: normalizeEvents(data.events),
        assignments: data.assignments || [],
        crew: data.crew || [],
        unavailability: normalizeUnavailability(data.unavailability || []),
        tours: data.tours || [],
        forecast: data.forecast || [],
        forecastError: data.forecastError ?? false,
        ...(quiet ? {} : { loading: false }),
      });
    } catch (err: any) {
      set(quiet ? { error: err.message } : { loading: false, error: err.message });
    }
  },

  // Quiet refetch helper shared by every mutation.
  // (inlined below as get().fetchWindow(..., { quiet: true }))

  assignVehicle: async (eventId, vehicleId, opts) => {
    // Refuse to assign onto a vehicle whose maintenance block covers the show's
    // travel window — the producer must clear the block first. (Backend enforces
    // this too; the pre-check just gives instant, clear feedback.)
    const evt = get().events.find(e => e.id === eventId);
    if (evt) {
      // A first assignment on a standalone show collapses its ship dates to the
      // show span server-side, so test the block against THAT window or we reject
      // an assignment the API would accept. Mirrors vehicle-schedule-service.js.
      const collapses = !evt.tour_id && !get().assignments.some(a => a.event_id === eventId);
      const showStart = evt.event_date;
      const showEnd = evt.end_date || evt.event_date;
      const wStart = collapses ? showStart : (evt.ship_out_date || showStart);
      const wEnd = collapses ? showEnd : (evt.ship_back_date || showEnd);
      const blocked = get().unavailability.some(
        u => u.asset_id === vehicleId && u.start_date <= wEnd && u.end_date >= wStart
      );
      if (blocked) return { ok: false, error: 'That vehicle has a maintenance block over those dates. Delete the block before assigning.' };
    }
    set({ saving: true, error: null });
    try {
      const res = await apiFetchAuthed('/vehicle-schedule/reassign', {
        method: 'POST',
        body: { changes: [{ action: 'add', event_id: eventId, vehicle_id: vehicleId }] },
        onUnauthorized: get()._onUnauthorized,
      });
      // /reassign answers 200 with { added, removed, collapsed, errors[] }, so a
      // REJECTED change never throws. The pre-check above only sees blocks
      // inside the fetched window, which is exactly how a rejection gets here:
      // a maintenance block on a date the grid has not loaded. Without this the
      // drop returned ok, the quiet refetch snapped the band back, and nothing
      // on screen said why. /move next door has always done this correctly.
      const failed = reassignError(res);
      if (failed) {
        set({ saving: false });
        return { ok: false, error: failed };
      }
      // transport_mode is NOT written here. A truck on a show with no external
      // transport flips the mode to 'driving' on the SERVER, inside the reassign
      // (api/src/events/driving-mode.js), so the Tour page's vehicle picker and a
      // show joining a covered tour get the same rule. On a show with freight
      // arranged the mode describes the freight and stays. The quiet refetch below
      // brings the result back.
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      if (opts?.detectTour !== false) get().detectTourProposal(vehicleId, eventId);
      return { ok: true };
    } catch (err: any) {
      set({ saving: false });
      return { ok: false, error: err.message };
    }
  },

  unassignVehicle: async (eventId, vehicleId) => {
    set({ saving: true, error: null });
    try {
      const res = await apiFetchAuthed('/vehicle-schedule/reassign', {
        method: 'POST',
        body: { changes: [{ action: 'remove', event_id: eventId, vehicle_id: vehicleId }] },
        onUnauthorized: get()._onUnauthorized,
      });
      const failed = reassignError(res);
      if (failed) {
        set({ saving: false });
        return { ok: false, error: failed };
      }
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      return { ok: true };
    } catch (err: any) {
      set({ saving: false });
      return { ok: false, error: err.message };
    }
  },

  setTransportArranged: async (eventId, arranged) => {
    set({ saving: true, error: null });
    // Optimistic flip so the band hops into / out of the bottom lanes immediately.
    set((s) => ({ events: s.events.map(e => (e.id === eventId ? { ...e, transport_arranged: arranged } : e)) }));
    // Clearing the flag on a show that keeps its truck makes the truck its
    // transport; the SERVER flips transport_mode to 'driving' inside this PATCH
    // (api/src/events/driving-mode.js), and the quiet refetch brings it back.
    try {
      await apiFetchAuthed(`/events/${eventId}`, {
        method: 'PATCH',
        body: { transport_arranged: arranged },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      return { ok: true };
    } catch (err: any) {
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true }).catch(() => {});
      set({ saving: false, error: err.message });
      return { ok: false, error: err.message };
    }
  },

  setTransportMode: async (eventId, mode) => {
    set({ saving: true, error: null });
    // Optimistic flavor flip so the band's legs relabel (ship-out vs leave HQ).
    set((s) => ({ events: s.events.map(e => (e.id === eventId ? { ...e, transport_mode: mode } : e)) }));
    try {
      await apiFetchAuthed(`/events/${eventId}`, {
        method: 'PATCH',
        body: { transport_mode: mode },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      return { ok: true };
    } catch (err: any) {
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true }).catch(() => {});
      set({ saving: false, error: err.message });
      return { ok: false, error: err.message };
    }
  },

  startBandDrag: (bandDrag) => set({ bandDrag }),
  endBandDrag: () => set({ bandDrag: null }),

  moveAssignment: async (payload, toVehicleId) => {
    if (payload.fromVehicleId === toVehicleId) return { ok: false, error: 'Already on that vehicle' };
    set({ saving: true, error: null });
    try {
      const res: { moved: number; scope: string } = await apiFetchAuthed('/vehicle-schedule/move', {
        method: 'POST',
        body: {
          event_id: payload.eventId,
          from_vehicle_id: payload.fromVehicleId,
          to_vehicle_id: toVehicleId,
          scope: payload.scope,
        },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      return { ok: true, moved: res?.moved, scope: res?.scope };
    } catch (err: any) {
      set({ saving: false, error: err.message });
      return { ok: false, error: err.message };
    }
  },

  startDrag: (drag) => set({ drag }),
  updateDrag: (previewDate, invalid) =>
    set((state) => (state.drag ? { drag: { ...state.drag, previewDate, invalid } } : {})),
  cancelDrag: () => set({ drag: null }),

  commitDrag: () => {
    const d = get().drag;
    set({ drag: null });
    if (!d || d.invalid) return;
    const field = d.edge === 'left' ? 'ship_out_date' : 'ship_back_date';
    // No-op if the edge didn't actually move.
    const ev = get().events.find(e => e.id === d.eventId);
    const current = field === 'ship_out_date' ? ev?.ship_out_date : ev?.ship_back_date;
    if (current === d.previewDate) return;
    (async () => {
      const res = await get().editShipDate(d.eventId, field, d.previewDate);
      // Rentals are ephemeral — never auto-propose tours for a rental-lane drag.
      if (res.ok && d.vehicleId !== RENTAL_VEHICLE_ID) get().detectTourProposal(d.vehicleId, d.eventId);
    })();
  },

  editShipDate: async (eventId, field, value) => {
    set({ saving: true, error: null });
    // Optimistic: move the band immediately so the drag doesn't snap back while
    // the PATCH + refetch round-trips. The refetch reconciles (or reverts on fail).
    set((s) => ({
      events: s.events.map(e => (e.id === eventId ? { ...e, [field]: value } : e)),
    }));
    try {
      await apiFetchAuthed(`/events/${eventId}`, {
        method: 'PATCH',
        body: { [field]: value },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      set({ saving: false });
      return { ok: true };
    } catch (err: any) {
      // Revert the optimistic move.
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true }).catch(() => {});
      set({ saving: false, error: err.message });
      return { ok: false, error: err.message };
    }
  },

  setTransportLegs: async (eventId, legs) => {
    set((s) => ({ events: s.events.map(e => (e.id === eventId ? { ...e, transport_out_note: legs.out, transport_back_note: legs.back } : e)) }));
    try {
      await apiFetchAuthed(`/events/${eventId}`, {
        method: 'PATCH',
        body: { transport_out_note: legs.out, transport_back_note: legs.back },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true }).catch(() => {});
      return { ok: false, error: err.message };
    }
  },

  updateAssignment: async (id, updates) => {
    try {
      await apiFetchAuthed(`/vehicle-schedule/assignment/${id}`, {
        method: 'PATCH',
        body: updates,
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  createBlock: async (assetId, startDate, endDate, notes) => {
    try {
      await apiFetchAuthed('/vehicle-schedule/unavailability', {
        method: 'POST',
        body: { asset_id: assetId, start_date: startDate, end_date: endDate, notes },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  deleteBlock: async (id) => {
    try {
      await apiFetchAuthed(`/vehicle-schedule/unavailability/${id}`, {
        method: 'DELETE',
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  detectTourProposal: (vehicleId, eventId) => {
    const { assignments, events, tours } = get();
    const eventById = new Map(events.map(e => [e.id, e] as const));
    // This vehicle's assigned events, with extents, sorted by leave date.
    const mine = assignments
      .filter(a => a.vehicle_id === vehicleId)
      .map(a => eventById.get(a.event_id))
      .filter((e): e is VehicleScheduleEvent => !!e)
      .map(e => ({ e, ...bandExtent(e) }))
      .sort((x, y) => (x.start < y.start ? -1 : x.start > y.start ? 1 : (x.e.id < y.e.id ? -1 : 1)));

    const idx = mine.findIndex(m => m.e.id === eventId);
    if (idx < 0) { set({ tourProposal: null }); return; }

    // A tour's MAIN vehicle = the one assigned to the most of its events. Auto-tour
    // only acts on the main vehicle; a meetup vehicle (covering just a slice of
    // someone else's tour) must never propose extending or dissolving that tour.
    const mainVehicleOfTour = (tourId: string): string | null => {
      const tourEventIds = new Set(events.filter(e => e.tour_id === tourId).map(e => e.id));
      const counts = new Map<string, number>();
      for (const a of assignments) {
        if (tourEventIds.has(a.event_id)) counts.set(a.vehicle_id, (counts.get(a.vehicle_id) || 0) + 1);
      }
      let best: string | null = null;
      let bestN = -1;
      for (const [vid, n] of counts) if (n > bestN) { best = vid; bestN = n; }
      return best;
    };

    // Two events (A earlier) are adjacent if their windows touch or overlap.
    const adjacent = (A: { end: string }, B: { start: string }) => A.end >= shiftDay(B.start, -1);

    // Expand the contiguous run containing the changed event.
    let lo = idx, hi = idx;
    while (lo > 0 && adjacent(mine[lo - 1], mine[lo])) lo--;
    while (hi < mine.length - 1 && adjacent(mine[hi], mine[hi + 1])) hi++;
    const run = mine.slice(lo, hi + 1).map(m => m.e);
    const tourName = (id: string) => tours.find(t => t.id === id)?.name ?? 'Tour';

    if (run.length >= 2) {
      const tourIds = [...new Set(run.map(e => e.tour_id).filter(Boolean))] as string[];
      if (tourIds.length === 0) {
        set({ tourProposal: { kind: 'create', vehicleId, eventIds: run.map(e => e.id), eventNames: run.map(e => e.name) } });
      } else if (tourIds.length === 1) {
        const untoured = run.filter(e => !e.tour_id);
        // Only the tour's main vehicle may absorb adjacent loose events.
        if (untoured.length > 0 && mainVehicleOfTour(tourIds[0]) === vehicleId) {
          set({ tourProposal: { kind: 'extend', vehicleId, tourId: tourIds[0], tourName: tourName(tourIds[0]), eventIds: untoured.map(e => e.id), eventNames: untoured.map(e => e.name) } });
        } else {
          set({ tourProposal: null }); // already grouped, or this is a meetup vehicle
        }
      } else {
        set({ tourProposal: null }); // run spans two tours — never auto-merge
      }
    } else {
      // Run is a lone event: if it's still flagged onto a tour AND this is the
      // tour's main vehicle, it's been pulled out of adjacency → propose removal.
      // A meetup vehicle leaving says nothing about the tour, so it's ignored.
      const E = mine[idx].e;
      if (E.tour_id && mainVehicleOfTour(E.tour_id) === vehicleId) {
        const tourEventCount = events.filter(e => e.tour_id === E.tour_id).length;
        set({ tourProposal: { kind: 'dissolve', vehicleId, tourId: E.tour_id, tourName: tourName(E.tour_id), eventId: E.id, eventName: E.name, willDissolve: tourEventCount - 1 < 2 } });
      } else {
        set({ tourProposal: null });
      }
    }
  },

  clearTourProposal: () => set({ tourProposal: null }),

  createTourFromEvents: async (eventIds) => {
    try {
      await apiFetchAuthed('/tours', {
        method: 'POST',
        body: { event_ids: eventIds },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  extendTour: async (tourId, eventIds) => {
    try {
      await apiFetchAuthed(`/tours/${tourId}/events`, {
        method: 'POST',
        body: { event_ids: eventIds },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  removeEventFromTour: async (tourId, eventId) => {
    try {
      // The endpoint auto-deletes a tour only at 0 events; our rule is "< 2
      // events dissolves". So if removing this event leaves exactly one, remove
      // that one too (→ 0 → auto-delete), reading the server's truth, not the
      // window (a tour may have events outside the visible range).
      const resp: any = await apiFetchAuthed(`/tours/${tourId}/events/${eventId}`, {
        method: 'DELETE',
        onUnauthorized: get()._onUnauthorized,
      });
      if (resp && !resp.deleted && Array.isArray(resp.events) && resp.events.length === 1) {
        await apiFetchAuthed(`/tours/${tourId}/events/${resp.events[0].id}`, {
          method: 'DELETE',
          onUnauthorized: get()._onUnauthorized,
        });
      }
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },

  splitTour: async (tourId, boundaryEventId, side) => {
    try {
      await apiFetchAuthed(`/tours/${tourId}/split`, {
        method: 'POST',
        body: { boundary_event_id: boundaryEventId, side },
        onUnauthorized: get()._onUnauthorized,
      });
      const { windowFrom, windowTo } = get();
      await get().fetchWindow(windowFrom, windowTo, undefined, { quiet: true });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message };
    }
  },
}));

export default useVehicleScheduleStore;
